import type { AppConfig } from './types';

// Brand tokens aligned with the Vardly homepage (vardly-homepage:
// static/css/custom.css `:root` block, mirrored in vardly-frontend/src/theme.ts
// where the ramps are named pine/brass), the canonical statement of the brand.
// Light-first: deep pine green (#1f6f5f) with a brass accent (#c8a24a) on soft
// green-tinted surfaces, Goldman display type for headings.
const GOLDMAN_DISPLAY = '"Goldman", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const SYSTEM_BODY =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const vardlyApp: AppConfig = {
    id: 'vardly',
    productName: 'Vardly',
    htmlTitle: 'Admin - Vardly',
    // The Vardly FDV app (vardly-frontend) is the companion GUI this orgadmin
    // instance manages organizations for — auth-backend registers its SPA client
    // (sirk-spa-vardly) against the same host.
    appLinkUrl: 'https://app.vardly.no',
    showBackToApp: true,
    logo: { src: '/apps/vardly/favicon.svg', alt: 'Vardly' },
    favicon: '/apps/vardly/favicon.svg',
    brand: {
        light: {
            // --vy-pine and --vy-brass on --vy-surface / --vy-surface-raised.
            primary: { main: '#1f6f5f', light: '#4f9d8b', dark: '#14483d', contrastText: '#ffffff' },
            secondary: { main: '#c8a24a', light: '#e0c98b', dark: '#9a7a2e', contrastText: '#1a1a1a' },
            background: { default: '#f2f7f5', paper: '#ffffff' },
        },
        dark: {
            // The homepage's [data-theme="dark"] overrides: lifted pine/brass on
            // deep green-tinted surfaces (--vy-surface / --vy-surface-raised).
            primary: { main: '#6cb5a1', light: '#8ec9ba', dark: '#4f9d8b', contrastText: '#0b1210' },
            secondary: { main: '#dcbb74', light: '#e8d29e', dark: '#c8a24a', contrastText: '#1a1a1a' },
            background: { default: '#101816', paper: '#16211e' },
        },
        // Goldman is the display face (--vy-display) for headings/wordmark only;
        // body text is the system sans stack, exactly as on the homepage.
        fonts: { heading: GOLDMAN_DISPLAY, body: SYSTEM_BODY },
        // The homepage hero (.vy-hero): a near-black scrim fading into brand pine.
        heroGradient: 'linear-gradient(100deg, #0b1210 0%, #14483d 60%, #1f6f5f 100%)',
        // The hero is dark even though the app is light-first, so the signup page
        // paints its foreground with the dark palette while the admin shell (and
        // the other pre-auth pages, which sit on background.default) stay light.
        heroThemeMode: 'dark',
    },
    defaultThemeMode: 'light',
    // Org self-management plus the FDV domain's admin surface on the Sirk
    // platform: locations and the logistics taxonomies (real estate, control
    // points), and solution deliveries for the top tier. Machines are not here:
    // they are a category tree the sirktek app already carries
    // (`category/machine`). Which of these a user actually sees is decided by
    // the JWT permissions their tier confers: the VARDLY_* plans sell assets,
    // categories and locations directly on their own ladder from rank 1 — no
    // plan includes another app's rank any more (see PERMISSION_SOLD_BY and
    // SubscriptionLevel.java) — while products/services are sold on vardly's own
    // rank 3.
    enabledResources: new Set([
        'settings',
        'billing',
        'user',
        'api-key',
        // Organization sharing, on the same gate as sirktek: the resource and its
        // menu entry require write:organization, which no app sells (see
        // PERMISSION_SOLD_BY), so every vardly tier carries it.
        'access-grant',
        'category/location',
        'category/realestate',
        'category/controlpoint',
        'product',
        'service',
    ]),
    // API key management is the rank-2 selling point: the api-key resource gates
    // on write:api:keys, which the backend strips below the rank each app sells
    // it at (vardly: 2 — see Permission.WRITE_API_KEYS in orgadmin-backend).
    signup: {
        // Tier ids must match the backend's SubscriptionLevel constants exactly:
        // the id is posted verbatim as `subscriptionLevel` at signup, and a value
        // no App claims creates no entitlement at all (see the ledger17 tier for
        // the failure mode). Every tier is billed per step of 50 active/stored
        // objects — the next 50 objects cost the same tier price again. An
        // "object" here is any object the app manages, whichever category tree it
        // sits in (properties, control points, machines) — deliberately phrased as
        // the whole catalog rather than an enumeration, so adding a tree does not
        // silently change what customers were quoted. The tier copy in
        // `src/i18n/*/subscription.ts` says the same, since customers compare the
        // cards one at a time. Display only — the backend does not meter steps yet.
        tiers: [
            {
                id: 'VARDLY_KONTROLL',
                i18nKey: 'vardlyKontroll',
                prices: { NOK: 750 },
                featureCount: 4,
                highlighted: false,
                metered: { objects: 50 },
            },
            {
                id: 'VARDLY_API_AI',
                i18nKey: 'vardlyApiAi',
                prices: { NOK: 1250 },
                featureCount: 3,
                highlighted: true,
                metered: { objects: 50 },
            },
            {
                id: 'VARDLY_REGNSKAP',
                i18nKey: 'vardlyRegnskap',
                prices: { NOK: 2500 },
                featureCount: 4,
                highlighted: false,
                metered: { objects: 50 },
            },
        ],
        // Norway only. Matches `countries.app.vardly` in sirk-orgadmin-backend,
        // which is the enforcing gate; this list decides what the picker offers.
        countries: ['no'],
    },
    // The logistics taxonomy carries the RealEstate and ControlPoint roots —
    // the FDV domain's category trees — so new vardly tenants opt in by default.
    // Not the machine taxonomy: a vardly tenant should not be opted into a tree
    // whose surface lives in the sirktek app, which they may not have.
    defaultTaxonomies: ['logistics'],
};
